import React, { useEffect, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import {
  UsersRound, Loader2, Copy, Dices, Crown, ArrowRight, LogOut, Trophy, Clock, Plus, LogIn, Sparkles, Lightbulb,
  History, Search, TrendingUp, Share2, Repeat, Download, Flame, Snowflake,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { api, fmt, fmtDelta, formatApiErrorDetail, CATEGORIES } from "../../lib/game";
import { useAuth } from "../../context/AuthContext";

const HH_KEY = "burgeon_household";

const loadStored = () => {
  try {
    return JSON.parse(localStorage.getItem(HH_KEY) || "null");
  } catch {
    return null;
  }
};

const Balances = ({ hh }) => (
  <div className="grid grid-cols-4 gap-2" data-testid="household-balances">
    {[
      { l: "Net Worth", v: hh.net_worth, c: "text-emerald-400" },
      { l: "Cash", v: hh.cash, c: "text-emerald-300" },
      { l: "Invest", v: hh.investments, c: "text-purple-300" },
      { l: "Debts", v: hh.debts, c: "text-red-300" },
    ].map((x) => (
      <div key={x.l} className="rounded-xl border border-slate-800 bg-slate-950/50 px-3 py-2 text-center">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{x.l}</p>
        <p className={`font-mono text-sm font-bold ${x.c}`}>{fmt(x.v)}</p>
      </div>
    ))}
  </div>
);

// Map a nickname -> {color, emoji} from a household's member list
const buildAvatarMap = (members = []) => {
  const map = {};
  members.forEach((m) => { map[m.nickname] = { color: m.color, emoji: m.emoji }; });
  return map;
};

// Small circular avatar bubble; falls back to the first letter of the name
const MemberAvatar = ({ av = {}, name = "", size = "sm" }) => {
  const dim = size === "lg" ? "w-9 h-9 text-lg" : size === "md" ? "w-8 h-8 text-base" : "w-7 h-7 text-sm";
  const color = av.color || "#334155";
  return (
    <span
      title={name}
      className={`${dim} rounded-full flex items-center justify-center shrink-0 border`}
      style={{ background: color + "33", borderColor: color + "80" }}
    >
      {av.emoji || (name ? name.charAt(0).toUpperCase() : "?")}
    </span>
  );
};

export const MembersLeaderboard = ({ decisions = [], members = [] }) => {
  const avatarOf = {};
  members.forEach((m) => { avatarOf[m.nickname] = { color: m.color, emoji: m.emoji }; });
  const swings = {};
  decisions.forEach((d) => {
    const imp = d.impact || {};
    const swing = (imp.cash || 0) + (imp.investments || 0) - (imp.debts || 0);
    if (!swings[d.player]) swings[d.player] = { player: d.player, swing: 0, turns: 0 };
    swings[d.player].swing += swing;
    swings[d.player].turns += 1;
  });
  const rows = Object.values(swings).sort((a, b) => b.swing - a.swing);
  if (rows.length < 2) return null;
  return (
    <div data-testid="members-leaderboard" className="rounded-3xl border border-slate-800 bg-slate-900/70 p-6">
      <h2 className="font-heading text-lg font-bold text-slate-100 mb-1">Player leaderboard</h2>
      <p className="text-xs text-slate-500 mb-4">Ranked by the net-worth swing of each player's turns.</p>
      <div className="space-y-2">
        {rows.map((r, idx) => {
          const av = avatarOf[r.player] || {};
          return (
            <div key={r.player} data-testid={`member-rank-${idx}`} className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-950/40 px-3 py-2.5">
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                idx === 0 ? "bg-yellow-400/20 text-yellow-300 border border-yellow-400/50"
                : idx === 1 ? "bg-slate-300/15 text-slate-200 border border-slate-400/40"
                : idx === 2 ? "bg-amber-600/20 text-amber-500 border border-amber-600/50"
                : "bg-slate-800 text-slate-500 border border-slate-700"}`}>
                {idx + 1}
              </span>
              <span className="w-8 h-8 rounded-full flex items-center justify-center text-base shrink-0 border border-slate-700"
                style={{ background: (av.color || "#334155") + "33" }}>
                {av.emoji || r.player.charAt(0).toUpperCase()}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-100 truncate">{r.player}</p>
                <p className="text-[11px] text-slate-500">{r.turns} turn{r.turns === 1 ? "" : "s"}</p>
              </div>
              <span className={`font-mono text-sm font-bold ${r.swing >= 0 ? "text-emerald-400" : "text-red-400"}`}>{fmtDelta(r.swing)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const AVATAR_COLORS = ["#ec4899", "#2dd4bf", "#f59e0b", "#60a5fa", "#a78bfa", "#34d399", "#f87171", "#fbbf24"];
const AVATAR_EMOJIS = ["😀", "😎", "🦊", "🐼", "🦁", "🐨", "🦉", "🐸", "🚀", "⭐", "🌟", "🍀"];

const AvatarPicker = ({ color, emoji, onColor, onEmoji, testid }) => (
  <div className="mt-3" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Your avatar</p>
    <div className="flex items-center gap-2 mb-2">
      <span className="w-9 h-9 rounded-full flex items-center justify-center text-lg border border-slate-600" style={{ background: color + "33" }}>{emoji}</span>
      <div className="flex flex-wrap gap-1.5">
        {AVATAR_COLORS.map((c) => (
          <button key={c} type="button" onClick={() => onColor(c)}
            className={`w-6 h-6 rounded-full border-2 transition-transform ${color === c ? "border-white scale-110" : "border-transparent"}`}
            style={{ background: c }} aria-label={`colour ${c}`} />
        ))}
      </div>
    </div>
    <div className="flex flex-wrap gap-1.5">
      {AVATAR_EMOJIS.map((e) => (
        <button key={e} type="button" onClick={() => onEmoji(e)}
          className={`w-7 h-7 rounded-lg text-base flex items-center justify-center border transition-colors ${emoji === e ? "border-pink-400 bg-pink-500/15" : "border-slate-700 bg-slate-950/40 hover:border-slate-600"}`}>
          {e}
        </button>
      ))}
    </div>
  </div>
);

export const PlayTogether = ({ onExit, onSignIn }) => {
  const { user } = useAuth();
  const loggedIn = user && typeof user === "object";
  const [stored, setStored] = useState(loadStored);
  const [hh, setHh] = useState(null);
  const [mode, setMode] = useState("home"); // home | loading
  const [busy, setBusy] = useState(false);
  const [hhName, setHhName] = useState("");
  const [nick, setNick] = useState("");
  const [joinName, setJoinName] = useState("");
  const [code, setCode] = useState("");
  const [myColor, setMyColor] = useState("#ec4899");
  const [myEmoji, setMyEmoji] = useState("😀");
  const [recap, setRecap] = useState(null);
  const [history, setHistory] = useState(null);
  const [lookupCode, setLookupCode] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [historyDetail, setHistoryDetail] = useState(null);
  const [detailRecap, setDetailRecap] = useState(null);
  const pollRef = useRef(null);
  const shareCardRef = useRef(null);
  const createRef = useRef(null);

  const playerId = stored?.player_id;

  const persist = (id, player_id) => {
    const s = { id, player_id };
    localStorage.setItem(HH_KEY, JSON.stringify(s));
    setStored(s);
  };

  const clearStored = () => {
    localStorage.removeItem(HH_KEY);
    setStored(null);
    setHh(null);
  };

  const refresh = useCallback(async () => {
    if (!stored?.id) return;
    try {
      const { data } = await api.get(`/households/${stored.id}?player_id=${stored.player_id}`);
      setHh(data);
    } catch (e) {
      clearStored();
    }
  }, [stored]);

  // initial load if we have a stored household
  useEffect(() => {
    if (stored?.id) {
      setMode("loading");
      refresh().finally(() => setMode("home"));
    }
  }, []); // eslint-disable-line

  // load the signed-in host's past households for the home view
  useEffect(() => {
    if (loggedIn && !stored?.id) {
      api.get("/me/households").then(({ data }) => setHistory(data)).catch(() => setHistory(null));
    }
  }, [loggedIn, stored]);

  const lookup = async () => {
    if (!lookupCode.trim()) return;
    setLookingUp(true);
    setLookupResult(null);
    try {
      const { data } = await api.get(`/households/code/${lookupCode.trim().toUpperCase()}`);
      setLookupResult(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No household with that code");
    } finally {
      setLookingUp(false);
    }
  };

  // Rejoin a past in-progress household as the host
  const rejoin = (h) => {
    persist(h.id, h.host_player_id);
    api.get(`/households/${h.id}?player_id=${h.host_player_id}`).then(({ data }) => setHh(data)).catch(() => {});
  };

  // Open a completed household's detail + AI recap
  const openHistory = async (h) => {
    setHistoryDetail("loading");
    setDetailRecap(null);
    try {
      const { data } = await api.get(`/households/${h.id}?player_id=${h.host_player_id || ""}`);
      setHistoryDetail(data);
      api.post(`/households/${h.id}/recap`).then(({ data }) => setDetailRecap(data)).catch(() => setDetailRecap(false));
    } catch (e) {
      toast.error("Couldn't open that game");
      setHistoryDetail(null);
    }
  };

  // Start a fresh game with the same household (host only)
  const rematch = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/households/${id}/rematch`);
      setHistoryDetail(null);
      persist(data.household.id, data.player_id);
      setHh(data.household);
      toast.success("New game ready — share the code with your household!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  // Share a completed result via the public code link
  const shareResult = async (hhObj) => {
    const url = `${window.location.origin}/api/og/household/${hhObj.code}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `${hhObj.name} on Burgeon`, text: `We finished with ${fmt(hhObj.net_worth)}! See how our household did:`, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Share link copied to clipboard");
      }
    } catch (e) {
      /* user cancelled share */
    }
  };

  // Download a pretty result card as an image for social sharing
  const downloadImage = async () => {
    if (!shareCardRef.current) return;
    try {
      const canvas = await html2canvas(shareCardRef.current, { backgroundColor: "#0B0F17", scale: 2 });
      const link = document.createElement("a");
      link.download = `burgeon-household.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Image saved");
    } catch (e) {
      toast.error("Couldn't create image");
    }
  };

  // Join the host's rematch in one tap (member device)
  const joinRematch = async (newCode, myNick) => {
    setBusy(true);
    try {
      const { data } = await api.post("/households/join", { code: newCode, nickname: myNick || "Player" });
      persist(data.household.id, data.player_id);
      setHh(data.household);
      setRecap(null);
      toast.success("Jumped into the rematch!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  // polling while in a household and not complete
  useEffect(() => {
    if (!stored?.id) return;
    pollRef.current = setInterval(refresh, 2500);
    return () => clearInterval(pollRef.current);
  }, [stored, refresh]);

  // fetch shared AI recap once the household finishes
  useEffect(() => {
    if (hh?.status === "complete" && stored?.id && recap === null) {
      setRecap("loading");
      api.post(`/households/${stored.id}/recap`)
        .then(({ data }) => setRecap(data))
        .catch(() => setRecap(false));
    }
  }, [hh, stored, recap]);

  const create = async () => {
    if (!hhName.trim() || !nick.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/households", { name: hhName.trim(), nickname: nick.trim(), color: myColor, emoji: myEmoji });
      persist(data.household.id, data.player_id);
      setHh(data.household);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const join = async () => {
    if (!code.trim() || !joinName.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/households/join", { code: code.trim().toUpperCase(), nickname: joinName.trim(), color: myColor, emoji: myEmoji });
      persist(data.household.id, data.player_id);
      setHh(data.household);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/households/${stored.id}/start`, { player_id: playerId });
      setHh(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const roll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/households/${stored.id}/roll`, { player_id: playerId });
      setHh(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      refresh();
    } finally {
      setBusy(false);
    }
  };

  const decide = async (choiceIndex) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/households/${stored.id}/decision`, {
        player_id: playerId,
        card_id: hh.current_card.id,
        choice_index: choiceIndex,
      });
      setHh(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      refresh();
    } finally {
      setBusy(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(hh.code);
    toast.success("Code copied");
  };

  // Scroll to and focus the create-a-household form
  const focusCreate = () => {
    createRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => createRef.current?.querySelector('[data-testid="hh-name-input"]')?.focus(), 400);
  };

  // ---------- HISTORY DETAIL (open a saved household) ----------
  if (historyDetail) {
    if (historyDetail === "loading") {
      return (
        <div className="min-h-[calc(100vh-61px)] flex items-center justify-center text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Opening game…
        </div>
      );
    }
    const hd = historyDetail;
    const change = hd.net_worth - (hd.starting_net_worth ?? 0);
    const hdAvatars = buildAvatarMap(hd.members);
    return (
      <div className="bg-grid min-h-[calc(100vh-61px)] px-4 py-8" data-testid="history-detail">
        <div className="max-w-2xl mx-auto space-y-5">
          <button data-testid="history-detail-back" onClick={() => { setHistoryDetail(null); setDetailRecap(null); }}
            className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-1.5">
            <ArrowRight className="w-4 h-4 rotate-180" /> Back to households
          </button>
          <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-7 text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-pink-400">Saved Household</p>
            <h1 className="font-heading text-3xl font-bold text-slate-50 mt-1">{hd.name}</h1>
            <p className="text-slate-400 text-sm mt-1">{hd.members.map((m) => m.nickname).join(" & ")}</p>
            <p className="font-mono text-5xl font-bold text-slate-50 mt-4">{fmt(hd.net_worth)}</p>
            <p className={`text-sm font-mono font-semibold mt-1 ${change >= 0 ? "text-emerald-400" : "text-red-400"}`}>
              {fmtDelta(change)} from {fmt(hd.starting_net_worth ?? 0)}
            </p>
            <div className="mt-5"><Balances hh={hd} /></div>
          </div>

          <div data-testid="history-detail-recap" className="rounded-3xl border border-pink-500/30 bg-gradient-to-b from-pink-500/[0.06] to-transparent bg-slate-900/70 p-6">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-pink-300" />
              <span className="text-xs font-semibold uppercase tracking-widest text-pink-300">Household Coach Recap</span>
            </div>
            {detailRecap === null ? (
              <div className="flex items-center gap-2 text-slate-400 text-sm py-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading recap…</div>
            ) : detailRecap === false ? (
              <p className="text-sm text-slate-400">Recap unavailable.</p>
            ) : (
              <>
                <p className="text-slate-200 leading-relaxed text-[15px]">{detailRecap.text}</p>
                {detailRecap.tip && (
                  <div className="mt-4 rounded-2xl border border-yellow-500/30 bg-yellow-500/[0.07] p-4 flex gap-3">
                    <Lightbulb className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
                    <p className="text-sm text-slate-200 leading-relaxed">{detailRecap.tip}</p>
                  </div>
                )}
              </>
            )}
          </div>

          <MembersLeaderboard decisions={hd.decisions} members={hd.members} />

          <div className="grid grid-cols-2 gap-3">
            <Button data-testid="detail-share-btn" onClick={() => shareResult(hd)}
              className="h-11 bg-gradient-to-r from-pink-500 to-rose-600 text-slate-950 font-bold rounded-xl">
              <Share2 className="w-4 h-4 mr-2" /> Share result
            </Button>
            <Button data-testid="detail-rematch-btn" onClick={() => rematch(hd.id)} disabled={busy}
              variant="outline" className="h-11 border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 font-semibold">
              <Repeat className="w-4 h-4 mr-2" /> Rematch
            </Button>
          </div>

          {hd.decisions?.length > 0 && (
            <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-6">
              <h2 className="font-heading text-lg font-bold text-slate-100 mb-4">Shared journey</h2>
              <div className="space-y-2">
                {hd.decisions.map((d, i) => {
                  const m = CATEGORIES[d.category] || CATEGORIES.SAVE;
                  const Icon = m.Icon;
                  return (
                    <div key={i} className="flex items-start gap-3 rounded-xl border border-slate-800 bg-slate-950/50 px-3 py-2.5">
                      <MemberAvatar av={hdAvatars[d.player]} name={d.player} size="md" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] font-mono text-slate-500">R{d.round}</span>
                          <span className="text-[11px] font-semibold text-pink-300">{d.player}</span>
                          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold ${m.text}`}>
                            <Icon className="w-3 h-3" /> {m.label}
                          </span>
                        </div>
                        <p className="text-sm text-slate-200 mt-0.5">{d.choice}</p>
                      </div>
                      <p className="text-[11px] font-mono font-semibold shrink-0 text-slate-400">{fmt(d.net_worth_after)}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---------- HOME (create / join) ----------
  if (!stored || (!hh && mode !== "loading")) {
    const lastCompleted = history?.households?.find((h) => h.status === "complete") || null;
    return (
      <div className="bg-grid min-h-[calc(100vh-61px)] px-4 py-10" data-testid="play-together-home">
        <div className="max-w-xl mx-auto">
          <div className="text-center mb-8">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center shadow-lg shadow-pink-500/25 mb-4">
              <UsersRound className="w-7 h-7 text-slate-950" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-slate-50">Play Together</h1>
            <p className="text-slate-400 text-sm mt-2 max-w-md mx-auto">
              Run one shared household budget with your partner or family. Take turns rolling and deciding — on any device.
            </p>
          </div>

          {loggedIn && history?.streak_lapsing && (
            <div data-testid="streak-nudge" className="rounded-3xl border border-orange-500/40 bg-gradient-to-b from-orange-500/[0.1] to-transparent bg-slate-900/70 p-5 mb-5">
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 shrink-0 rounded-xl bg-orange-500/20 border border-orange-500/40 flex items-center justify-center">
                  <Flame className="w-5 h-5 text-orange-300" />
                </span>
                <div className="flex-1 min-w-0">
                  <h2 className="font-heading text-base font-bold text-slate-100">Fancy another round together?</h2>
                  <p className="text-sm text-slate-300 mt-0.5">
                    Your <span className="font-semibold text-orange-300">{history.household_streak}-day</span> play-together streak keeps going if you play today. Don't let it slip!
                  </p>
                  {history.household_freeze_tokens > 0 && (
                    <p data-testid="streak-nudge-freeze" className="text-xs text-sky-300/90 mt-1.5 inline-flex items-center gap-1.5">
                      <Snowflake className="w-3.5 h-3.5" /> You have a streak freeze — it'll cover one busy day if you can't play.
                    </p>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button data-testid="streak-nudge-new-btn" onClick={focusCreate}
                      className="h-9 px-4 bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-400 hover:to-rose-500 text-slate-950 font-bold text-sm rounded-xl">
                      <Plus className="w-4 h-4 mr-1.5" /> Start a new game
                    </Button>
                    {lastCompleted && (
                      <Button data-testid="streak-nudge-rematch-btn" onClick={() => rematch(lastCompleted.id)} disabled={busy}
                        variant="outline" className="h-9 px-4 border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 font-semibold text-sm rounded-xl">
                        <Repeat className="w-4 h-4 mr-1.5" /> Rematch {lastCompleted.name}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div ref={createRef} className="rounded-3xl border border-slate-800 bg-slate-900/70 p-6">
            <h2 className="font-heading text-lg font-bold text-slate-100 flex items-center gap-2">
              <Plus className="w-5 h-5 text-pink-400" /> Create a household
            </h2>
            {loggedIn ? (
              <>
                <Input data-testid="hh-name-input" value={hhName} onChange={(e) => setHhName(e.target.value)} placeholder="Household name (e.g. The Taylors)" maxLength={60}
                  className="mt-3 bg-slate-950/60 border-slate-700 text-slate-100 h-11" />
                <Input data-testid="hh-nick-input" value={nick} onChange={(e) => setNick(e.target.value)} placeholder="Your name" maxLength={24}
                  className="mt-2 bg-slate-950/60 border-slate-700 text-slate-100 h-11" />
                <AvatarPicker color={myColor} emoji={myEmoji} onColor={setMyColor} onEmoji={setMyEmoji} testid="hh-create-avatar" />
                <Button data-testid="hh-create-btn" onClick={create} disabled={!hhName.trim() || !nick.trim() || busy}
                  className="mt-3 w-full h-11 bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-400 hover:to-rose-500 text-slate-950 font-bold rounded-xl disabled:opacity-50">
                  {busy ? "Creating…" : "Create & get a code"}
                </Button>
              </>
            ) : (
              <div className="mt-3 rounded-2xl border border-slate-700 bg-slate-950/50 px-4 py-4 text-center">
                <p className="text-sm text-slate-300">Sign in to create a household and save your shared progress over time.</p>
                <Button data-testid="hh-create-signin-btn" onClick={onSignIn} className="mt-3 bg-slate-100 text-slate-900 hover:bg-white font-semibold">
                  <LogIn className="w-4 h-4 mr-1.5" /> Sign In to host
                </Button>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-slate-800" />
            <span className="text-xs uppercase tracking-widest text-slate-600 font-semibold">or</span>
            <div className="h-px flex-1 bg-slate-800" />
          </div>

          <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-6">
            <h2 className="font-heading text-lg font-bold text-slate-100 flex items-center gap-2">
              <LogIn className="w-5 h-5 text-teal-400" /> Join a household
            </h2>
            <Input data-testid="hh-join-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="6-character code" maxLength={6}
              className="mt-3 bg-slate-950/60 border-slate-700 text-slate-100 h-11 font-mono tracking-widest placeholder:tracking-normal" />
            <Input data-testid="hh-join-nick-input" value={joinName} onChange={(e) => setJoinName(e.target.value)} placeholder="Your name" maxLength={24}
              className="mt-2 bg-slate-950/60 border-slate-700 text-slate-100 h-11" />
            <AvatarPicker color={myColor} emoji={myEmoji} onColor={setMyColor} onEmoji={setMyEmoji} testid="hh-join-avatar" />
            <Button data-testid="hh-join-btn" onClick={join} disabled={!code.trim() || !joinName.trim() || busy}
              className="mt-3 w-full h-11 bg-slate-100 text-slate-900 hover:bg-white font-bold rounded-xl disabled:opacity-50">
              {busy ? "Joining…" : "Join household"}
            </Button>
          </div>

          {/* Look up a past game by code */}
          <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-6 mt-5">
            <h2 className="font-heading text-lg font-bold text-slate-100 flex items-center gap-2">
              <Search className="w-5 h-5 text-teal-400" /> View a past game by code
            </h2>
            <div className="mt-3 flex gap-2">
              <Input data-testid="hh-lookup-input" value={lookupCode} onChange={(e) => setLookupCode(e.target.value.toUpperCase())} placeholder="Household code" maxLength={6}
                className="bg-slate-950/60 border-slate-700 text-slate-100 h-11 font-mono tracking-widest placeholder:tracking-normal" />
              <Button data-testid="hh-lookup-btn" onClick={lookup} disabled={!lookupCode.trim() || lookingUp}
                className="h-11 px-5 bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold shrink-0">
                {lookingUp ? "…" : "Look up"}
              </Button>
            </div>
            {lookupResult && (
              <div data-testid="hh-lookup-result" className="mt-3 rounded-2xl border border-slate-700 bg-slate-950/50 px-4 py-3">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-100">{lookupResult.name}</span>
                  <span className="font-mono text-lg font-bold text-emerald-400">{fmt(lookupResult.net_worth)}</span>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  {lookupResult.members.map((m) => m.nickname).join(" & ")} ·{" "}
                  {lookupResult.status === "complete"
                    ? `${fmtDelta(lookupResult.net_worth - (lookupResult.starting_net_worth ?? 0))} over ${lookupResult.total_rounds} rounds`
                    : `In progress — round ${lookupResult.current_round}`}
                </p>
              </div>
            )}
          </div>

          {/* Signed-in host's shared household history */}
          {loggedIn && history && history.households.length > 0 && (
            <div data-testid="household-history" className="rounded-3xl border border-slate-800 bg-slate-900/70 p-6 mt-5">
              <div className="flex items-center justify-between gap-3">
                <h2 className="font-heading text-lg font-bold text-slate-100 flex items-center gap-2">
                  <History className="w-5 h-5 text-pink-400" /> Your households
                </h2>
                <div className="flex items-center gap-2">
                  {history.household_streak > 0 && (
                    <span data-testid="household-streak" className="inline-flex items-center gap-1.5 rounded-full border border-orange-500/40 bg-orange-500/10 px-3 py-1 text-sm font-semibold text-orange-300">
                      <Flame className="w-4 h-4" /> {history.household_streak}-day play-together streak
                    </span>
                  )}
                  {history.household_freeze_tokens > 0 && (
                    <span data-testid="household-freeze" title="One busy day won't break your streak" className="inline-flex items-center gap-1.5 rounded-full border border-sky-500/40 bg-sky-500/10 px-3 py-1 text-sm font-semibold text-sky-300">
                      <Snowflake className="w-4 h-4" /> {history.household_freeze_tokens} streak freeze
                    </span>
                  )}
                </div>
              </div>
              {history.trend.length > 1 && (
                <div className="mt-4">
                  <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-emerald-400 mb-2">
                    <TrendingUp className="w-4 h-4" /> Joint net worth over time
                  </p>
                  <div style={{ width: "100%", height: 180 }}>
                    <ResponsiveContainer>
                      <LineChart data={history.trend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                        <XAxis dataKey="date" tick={{ fill: "#94a3b8", fontSize: 10 }} />
                        <YAxis tick={{ fill: "#94a3b8", fontSize: 10 }} />
                        <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 12, color: "#e2e8f0" }} formatter={(v) => fmt(v)} />
                        <Line type="monotone" dataKey="net_worth" stroke="#ec4899" strokeWidth={2.5} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
              <div className="mt-4 space-y-2">
                {history.households.map((h) => {
                  const complete = h.status === "complete";
                  return (
                    <div key={h.id} data-testid={`history-item-${h.id}`}
                      className="flex items-center justify-between gap-3 rounded-xl border border-slate-800 bg-slate-950/40 px-3 py-2.5">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-100 truncate">{h.name}</p>
                        <p className="text-xs text-slate-500">
                          {h.members.join(" & ")} · <span className="font-mono">{h.code}</span> ·{" "}
                          {complete ? "Complete" : `Round ${h.current_round}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="font-mono text-sm font-bold text-emerald-400">{fmt(h.net_worth)}</span>
                        {complete ? (
                          <Button data-testid={`history-view-${h.id}`} onClick={() => openHistory(h)}
                            variant="outline" className="h-8 px-3 text-xs border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">
                            View recap
                          </Button>
                        ) : (
                          <Button data-testid={`history-rejoin-${h.id}`} onClick={() => rejoin(h)}
                            className="h-8 px-3 text-xs bg-pink-500 hover:bg-pink-400 text-slate-950 font-semibold">
                            Rejoin
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {history.households.filter((h) => h.status === "complete").length > 1 && (
                <div data-testid="household-leaderboard" className="mt-5 rounded-2xl border border-yellow-500/25 bg-yellow-500/[0.05] p-4">
                  <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-yellow-400 mb-3">
                    <Trophy className="w-4 h-4" /> Family leaderboard — best finishes
                  </p>
                  <div className="space-y-1.5">
                    {history.households
                      .filter((h) => h.status === "complete")
                      .sort((a, b) => b.net_worth - a.net_worth)
                      .slice(0, 5)
                      .map((h, idx) => (
                        <div key={h.id} data-testid={`leaderboard-row-${idx}`} className="flex items-center gap-3 text-sm">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            idx === 0 ? "bg-yellow-400/20 text-yellow-300 border border-yellow-400/50"
                            : idx === 1 ? "bg-slate-300/15 text-slate-200 border border-slate-400/40"
                            : idx === 2 ? "bg-amber-600/20 text-amber-500 border border-amber-600/50"
                            : "bg-slate-800 text-slate-500 border border-slate-700"}`}>
                            {idx + 1}
                          </span>
                          <span className="flex-1 text-slate-200 truncate">{h.name}</span>
                          <span className="font-mono font-bold text-emerald-400">{fmt(h.net_worth)}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!hh) {
    return (
      <div className="min-h-[calc(100vh-61px)] flex items-center justify-center text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading household…
      </div>
    );
  }

  const meta = hh.current_card ? CATEGORIES[hh.current_card.category] || CATEGORIES.SAVE : null;

  // ---------- LOBBY ----------
  if (hh.status === "lobby") {
    return (
      <div className="bg-grid min-h-[calc(100vh-61px)] px-4 py-10" data-testid="household-lobby">
        <div className="max-w-xl mx-auto">
          <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-6 sm:p-8 text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-pink-400">Household Lobby</p>
            <h1 className="font-heading text-2xl font-bold text-slate-50 mt-1">{hh.name}</h1>
            <p className="text-slate-400 text-sm mt-1">Share this code so others can join on their device.</p>

            <button onClick={copyCode} data-testid="household-code" className="mt-4 inline-flex items-center gap-3 rounded-2xl border border-pink-500/40 bg-pink-500/10 px-6 py-3 hover:bg-pink-500/15 transition-colors">
              <span className="font-mono text-3xl font-bold tracking-[0.3em] text-pink-200">{hh.code}</span>
              <Copy className="w-5 h-5 text-pink-300" />
            </button>

            <div className="mt-6 text-left">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                Members ({hh.member_count})
              </p>
              <div className="space-y-2" data-testid="household-members">
                {hh.members.map((m, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-950/40 px-3 py-2">
                    <span className="w-8 h-8 rounded-full flex items-center justify-center text-base border" style={{ background: (m.color || "#ec4899") + "33", borderColor: (m.color || "#ec4899") + "80" }}>
                      {m.emoji || m.nickname.charAt(0).toUpperCase()}
                    </span>
                    <span className="text-sm text-slate-200 flex-1">{m.nickname}{m.nickname === hh.my_nickname ? " (you)" : ""}</span>
                    {m.is_host && <Crown className="w-4 h-4 text-yellow-400" title="Host" />}
                  </div>
                ))}
              </div>
            </div>

            {hh.is_host ? (
              <Button data-testid="household-start-btn" onClick={start} disabled={hh.member_count < 2 || busy}
                className="mt-6 w-full h-12 bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-400 hover:to-rose-500 text-slate-950 font-bold rounded-xl disabled:opacity-50">
                {hh.member_count < 2 ? "Waiting for another member…" : busy ? "Starting…" : "Start playing together"}
                {hh.member_count >= 2 && <ArrowRight className="w-5 h-5 ml-1" />}
              </Button>
            ) : (
              <p className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-400">
                <Clock className="w-4 h-4" /> Waiting for the host to start…
              </p>
            )}

            <button data-testid="household-leave-btn" onClick={() => { clearStored(); onExit(); }} className="mt-4 text-xs text-slate-500 hover:text-red-400 inline-flex items-center gap-1">
              <LogOut className="w-3.5 h-3.5" /> Leave household
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- COMPLETE ----------
  if (hh.status === "complete") {
    const change = hh.net_worth - (hh.starting_net_worth ?? 0);
    const hhAvatars = buildAvatarMap(hh.members);
    return (
      <div className="bg-grid min-h-[calc(100vh-61px)] px-4 py-8" data-testid="household-complete">
        <div className="max-w-2xl mx-auto space-y-5">
          <div ref={shareCardRef} className="rounded-3xl border border-slate-800 bg-slate-900/70 p-7 text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <UsersRound className="w-4 h-4 text-slate-950" />
              </div>
              <span className="font-heading text-base font-bold text-slate-100">Burgeon · Play Together</span>
            </div>
            <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center mb-4">
              <Trophy className="w-7 h-7 text-slate-950" />
            </div>
            <p className="text-xs font-semibold uppercase tracking-widest text-pink-400">Household Complete</p>
            <h1 className="font-heading text-3xl font-bold text-slate-50 mt-1">{hh.name}</h1>
            <p className="font-mono text-5xl font-bold text-slate-50 mt-4" data-testid="household-final-net-worth">{fmt(hh.net_worth)}</p>
            <p className={`text-sm font-mono font-semibold mt-1 ${change >= 0 ? "text-emerald-400" : "text-red-400"}`}>
              {fmtDelta(change)} from your starting {fmt(hh.starting_net_worth ?? 0)}
            </p>
            <div className="mt-5"><Balances hh={hh} /></div>
          </div>

          <div data-testid="household-recap" className="rounded-3xl border border-pink-500/30 bg-gradient-to-b from-pink-500/[0.06] to-transparent bg-slate-900/70 p-6">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-pink-300" />
              <span className="text-xs font-semibold uppercase tracking-widest text-pink-300">Your Household Coach Recap</span>
            </div>
            {recap === "loading" || recap === null ? (
              <div className="flex items-center gap-2 text-slate-400 text-sm py-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Writing your shared recap…
              </div>
            ) : recap === false ? (
              <p className="text-sm text-slate-400">Recap unavailable right now.</p>
            ) : (
              <>
                <p className="text-slate-200 leading-relaxed text-[15px]">{recap.text}</p>
                {recap.tip && (
                  <div className="mt-4 rounded-2xl border border-yellow-500/30 bg-yellow-500/[0.07] p-4 flex gap-3">
                    <Lightbulb className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-yellow-400 mb-0.5">Tip for your household</p>
                      <p className="text-sm text-slate-200 leading-relaxed">{recap.tip}</p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-6">
            <h2 className="font-heading text-lg font-bold text-slate-100 mb-4">Your shared journey</h2>
            <div className="space-y-2">
              {hh.decisions.map((d, i) => {
                const m = CATEGORIES[d.category] || CATEGORIES.SAVE;
                const Icon = m.Icon;
                return (
                  <div key={i} className="flex items-start gap-3 rounded-xl border border-slate-800 bg-slate-950/50 px-3 py-2.5">
                    <MemberAvatar av={hhAvatars[d.player]} name={d.player} size="md" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] font-mono text-slate-500">R{d.round}</span>
                        <span className="text-[11px] font-semibold text-pink-300">{d.player}</span>
                        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold ${m.text}`}>
                          <Icon className="w-3 h-3" /> {m.label}
                        </span>
                        {d.is_joint && <span className="text-[9px] font-bold uppercase tracking-wide text-teal-300 border border-teal-500/40 rounded px-1">Joint</span>}
                      </div>
                      <p className="text-sm text-slate-200 mt-0.5">{d.choice}</p>
                    </div>
                    <p className={`text-[11px] font-mono font-semibold shrink-0 ${d.net_worth_after >= (hh.starting_net_worth ?? 0) ? "text-emerald-400" : "text-slate-400"}`}>
                      {fmt(d.net_worth_after)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          <MembersLeaderboard decisions={hh.decisions} members={hh.members} />

          {hh.next_household_code && !hh.is_host && (
            <div data-testid="rematch-nudge" className="rounded-2xl border border-pink-500/50 bg-pink-500/10 p-4 flex items-center gap-3">
              <Repeat className="w-5 h-5 text-pink-300 shrink-0" />
              <p className="text-sm text-slate-200 flex-1">{hh.current_turn_nickname || "The host"} started a rematch — jump back in!</p>
              <Button data-testid="rematch-join-btn" onClick={() => joinRematch(hh.next_household_code, hh.my_nickname)} disabled={busy}
                className="bg-pink-500 hover:bg-pink-400 text-slate-950 font-semibold shrink-0 h-9">
                Join <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Button data-testid="household-share-btn" onClick={() => shareResult(hh)}
              className="h-12 bg-gradient-to-r from-pink-500 to-rose-600 text-slate-950 font-bold rounded-xl">
              <Share2 className="w-5 h-5 mr-2" /> Share link
            </Button>
            <Button data-testid="household-download-btn" onClick={downloadImage}
              variant="outline" className="h-12 border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 font-semibold rounded-xl">
              <Download className="w-5 h-5 mr-2" /> Download image
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {hh.is_host && (
              <Button data-testid="household-rematch-btn" onClick={() => rematch(hh.id)} disabled={busy}
                variant="outline" className="h-11 border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 font-semibold">
                <Repeat className="w-4 h-4 mr-2" /> Rematch
              </Button>
            )}
            <Button data-testid="household-finish-btn" onClick={() => { clearStored(); onExit(); }}
              className={`h-11 bg-slate-800 hover:bg-slate-700 text-slate-100 font-semibold rounded-xl ${hh.is_host ? "" : "col-span-2"}`}>
              Finish
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- PLAYING ----------
  const myTurn = hh.is_my_turn;
  const card = hh.current_card;
  const currentMember = hh.members.find((m) => m.nickname === hh.current_turn_nickname) || {};
  return (
    <div className="bg-grid min-h-[calc(100vh-61px)] px-4 py-8" data-testid="household-board">
      <div className="max-w-2xl mx-auto space-y-5">
        <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-5">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-pink-400">{hh.name}</p>
              <p className="text-sm text-slate-400">Round {hh.current_round} of {hh.total_rounds}</p>
            </div>
            <div className="flex -space-x-2">
              {hh.members.map((m, i) => (
                <span key={i} title={m.nickname}
                  className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-base ${
                    m.nickname === hh.current_turn_nickname ? "scale-110" : "opacity-60"
                  }`}
                  style={{ background: (m.color || "#334155") + "33", borderColor: m.nickname === hh.current_turn_nickname ? (m.color || "#ec4899") : "#1e293b" }}>
                  {m.emoji || m.nickname.charAt(0).toUpperCase()}
                </span>
              ))}
            </div>
          </div>
          <Balances hh={hh} />
        </div>

        <div className={`rounded-2xl border px-4 py-3 flex items-center justify-center gap-2.5 text-sm font-semibold ${myTurn ? "border-pink-500/50 bg-pink-500/10 text-pink-200" : "border-slate-800 bg-slate-900/50 text-slate-400"}`} data-testid="turn-indicator">
          <MemberAvatar av={{ color: currentMember.color, emoji: currentMember.emoji }} name={hh.current_turn_nickname} size="sm" />
          {myTurn ? "It's your turn!" : `Waiting for ${hh.current_turn_nickname}…`}
        </div>

        <AnimatePresence mode="wait">
          {!card ? (
            <motion.div key="roll" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="rounded-3xl border border-slate-800 bg-slate-900/70 p-10 text-center">
              {myTurn ? (
                <>
                  <p className="text-slate-300 mb-5">Roll the dice to draw your household's next scenario.</p>
                  <Button data-testid="household-roll-btn" onClick={roll} disabled={busy}
                    className="h-14 px-10 bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-400 hover:to-rose-500 text-slate-950 font-bold text-lg rounded-2xl">
                    <Dices className="w-6 h-6 mr-2" /> {busy ? "Rolling…" : "Roll the dice"}
                  </Button>
                </>
              ) : (
                <div className="flex items-center justify-center gap-2 text-slate-400">
                  <Loader2 className="w-5 h-5 animate-spin" /> {hh.current_turn_nickname} is about to roll…
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div key="card" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              data-testid="household-card"
              className={`rounded-3xl border ${meta.border} bg-slate-900/70 overflow-hidden`}>
              <div className={`px-5 py-3 flex items-center gap-2 ${meta.bg} border-b ${meta.border}`}>
                <meta.Icon className={`w-4 h-4 ${meta.text}`} />
                <span className={`text-xs font-bold uppercase tracking-wider ${meta.text}`}>{meta.label}</span>
                <span className="ml-auto flex items-center gap-1.5">
                  <MemberAvatar av={{ color: currentMember.color, emoji: currentMember.emoji }} name={hh.current_turn_nickname} size="sm" />
                  <span className="text-[11px] font-semibold text-slate-300">{myTurn ? "You" : hh.current_turn_nickname}</span>
                </span>
                {card.is_joint && <span className="text-[10px] font-bold uppercase tracking-wide text-teal-300 border border-teal-500/40 rounded px-1.5 py-0.5">Joint household</span>}
              </div>
              <div className="p-5 sm:p-6">
                <p className="text-slate-200 leading-relaxed">{card.scenario_text}</p>
                <div className="mt-5 space-y-2.5">
                  {card.choices.map((ch, i) => (
                    <button
                      key={i}
                      data-testid={`household-choice-${i}`}
                      disabled={!myTurn || busy}
                      onClick={() => decide(i)}
                      className={`w-full text-left rounded-2xl border px-4 py-3 transition-all ${
                        myTurn && !busy
                          ? "border-slate-700 bg-slate-950/50 hover:border-pink-500/50 hover:bg-pink-500/[0.07] cursor-pointer"
                          : "border-slate-800 bg-slate-950/30 opacity-70 cursor-not-allowed"
                      }`}
                    >
                      <p className="text-sm font-medium text-slate-100">{ch.text}</p>
                      {ch.note && <p className="text-xs text-slate-500 mt-0.5">{ch.note}</p>}
                    </button>
                  ))}
                </div>
                {!myTurn && (
                  <p className="mt-4 flex items-center justify-center gap-2 text-sm text-slate-400">
                    <Clock className="w-4 h-4" /> Waiting for {hh.current_turn_nickname} to decide…
                  </p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="text-center">
          <button data-testid="household-leave-playing-btn" onClick={() => { clearStored(); onExit(); }} className="text-xs text-slate-600 hover:text-red-400 inline-flex items-center gap-1">
            <LogOut className="w-3.5 h-3.5" /> Leave game
          </button>
        </div>
      </div>
    </div>
  );
};
